import { useLocation, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'

import { figureFromHash, rowFromHash, sameFigure } from '../lib/figure-focus.ts'

/** How long a figure or a row that has not rendered yet is waited for. */
const WAIT_MS = 10000

/** How long the highlight stays before the anchor is removed. */
const HIGHLIGHT_MS = 2000

function findFigure(figure: string): HTMLElement[] {
  const found = Array.from(
    document.querySelectorAll<HTMLElement>('[data-figure]')
  ).find(element => sameFigure(element.dataset.figure ?? '', figure))
  return found === undefined ? [] : [found]
}

// One customer on two accounts is two rows, and both are the one asked for.
function findRows(row: string): HTMLElement[] {
  return Array.from(
    document.querySelectorAll<HTMLElement>('[data-row-anchor]')
  ).filter(element => element.dataset.rowAnchor === row)
}

/** Reveal a search result's figure or table row, including one loaded after navigation. */
export function useFigureFocus(): void {
  const location = useLocation()
  const navigate = useNavigate()
  // The router partly decodes location.hash. Read the original anchor.
  const hashStart = location.href.indexOf('#')
  const hash = hashStart === -1 ? '' : location.href.slice(hashStart)
  const figure = figureFromHash(hash)
  const row = rowFromHash(hash)

  // Keyed on the anchor, so a sort or a filter inside the highlight does not
  // scroll the reader back.
  useEffect(() => {
    let find: () => HTMLElement[]
    if (figure !== null) find = () => findFigure(figure)
    else if (row !== null) find = () => findRows(row)
    else return undefined
    let focused: HTMLElement[] = []
    let addedTabIndex = false
    let highlightTimer: ReturnType<typeof setTimeout> | undefined

    function release(): void {
      for (const element of focused)
        element.removeAttribute('data-figure-focus')
      if (addedTabIndex) focused[0]?.removeAttribute('tabindex')
      focused = []
      addedTabIndex = false
    }

    function reveal(first: HTMLElement, targets: HTMLElement[]): void {
      release()
      focused = targets
      const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)')
        ?.matches
        ? 'auto'
        : 'smooth'
      first.scrollIntoView({ block: 'center', inline: 'center', behavior })
      for (const element of targets)
        element.setAttribute('data-figure-focus', '')
      // A keyboard or screen reader user lands where the reader's eyes do.
      if (!first.hasAttribute('tabindex')) {
        first.setAttribute('tabindex', '-1')
        addedTabIndex = true
      }
      first.focus({ preventScroll: true })
    }

    function check(): void {
      // A pending tile or header can be replaced by its loaded node while the
      // highlight shows, so the observer runs until the highlight ends.
      if (focused.length > 0 && focused.every(element => element.isConnected))
        return
      const targets = find()
      const [first] = targets
      if (first === undefined) return
      reveal(first, targets)
      if (highlightTimer !== undefined) return
      clearTimeout(deadline)
      highlightTimer = setTimeout(() => {
        observer.disconnect()
        release()
        void navigate({
          hash: '',
          replace: true,
          search: true,
          resetScroll: false
        })
      }, HIGHLIGHT_MS)
    }

    const observer = new MutationObserver(check)
    const deadline = setTimeout(() => observer.disconnect(), WAIT_MS)
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-figure', 'data-row-anchor']
    })
    check()
    return () => {
      observer.disconnect()
      clearTimeout(deadline)
      clearTimeout(highlightTimer)
      release()
    }
  }, [figure, row, navigate])
}
